import React, { FC, useEffect, useRef, useState } from "react";
import { LandPlot } from "../../../../interfaces/card";
import styles from "./card-multiple-plot-list.module.scss";
import Button from "../../../ui-kit/button/button";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { useTranslation } from "react-i18next";
import { getCutAndFill } from "../../../../services/api";
import { CutAndFill } from "../../../../interfaces/feature-info";
import { createPortal } from "react-dom";
import CardSummary from "../../filters/card-summary/card-summary";
import { LayersDataTypes } from "../../../../types/layers-data-types";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import useGeneratePdf from "../../../../hooks/useGeneratePdf";
import useInfrastructure from "../../../../hooks/useInfrastructure";
import {
  saveScreenshot,
  setLayersVisible,
} from "../../../../services/reducers/map";
import {
  focusOnFeature,
  takeScreenshot,
} from "../../../../services/actions/map";
import { LayerType } from "../../../../types/layer-type";
import positionIcon from "../../../../images/position.svg";

interface CardMultiplePlotList {
  data: LandPlot;
}

const CardMultiplePlotList: FC<CardMultiplePlotList> = ({ data }) => {
  const { t } = useTranslation();
  const numbersArray = data.number.split(", ");
  const label =
    numbersArray.length > 1
      ? t("cardMultiplePlotList.plotNumbers")
      : t("cardMultiplePlotList.plotNumber");
  const { finalMultiplePlot, generalGeometry } = useAppSelector(
    (state) => state.multiplePlotList,
  );
  const { layersIds, visibleLayersId } = useAppSelector((state) => state.map);
  const [cutAndFillImage, setCutAndFillImage] = useState<string | null>(null);
  const [cutAndFill, setCutAndFill] = useState<CutAndFill | null>(null);
  const [isPdfVisible, setIsPdfVisible] = useState<boolean>(false);
  const pdfPortal = document.getElementById("pdf-portal");
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const layers = useAppSelector((state) => state.map.layers);
  const dispatch = useAppDispatch();
  const screenshot = useAppSelector((state) => state.map.screenshot);
  const generatePdf = useGeneratePdf();
  const infrastructureData = useInfrastructure(data.geometry);
  const plotsLayerIds = layersIds[LayersDataTypes.Plots] ?? [];

  const handleClickGetSummary = () => {
    setCutAndFillImage(null);
    setCutAndFill(null);
    dispatch(saveScreenshot(null));

    const missingIds = plotsLayerIds.filter(
      (id) => !visibleLayersId.includes(id),
    );

    if (missingIds.length > 0) {
      dispatch(setLayersVisible([...visibleLayersId, ...missingIds]));
    }

    setIsPdfVisible(true);

    getCutAndFill({ geometry: finalMultiplePlot?.geometry })
      .then((res) => {
        if (res) {
          setCutAndFill({ landCut: res.landCut, landFill: res.landFill });
          setCutAndFillImage(`data:image/jpg;base64,${res.image}`);
        } else {
          setCutAndFill(null);
          setCutAndFillImage(null);
        }
      })
      .catch(() => {
        setCutAndFill(null);
        setCutAndFillImage(null);
      });

    if (data.geometry) {
      dispatch(
        takeScreenshot({
          name: `${data.objectId}`,
          width: 1602,
          height: 1326,
          layers: layers.filter((l) =>
            layers
              .filter(
                (x) =>
                  x.dataType === LayersDataTypes.Plots &&
                  x.type === LayerType.ArcgisFeatureServer,
              )
              .map((x) => x.parentId ?? x.id)
              .includes(l.parentId ?? l.id),
          ),
          geometry: data.geometry,
        }),
      );
    }
  };

  useEffect(() => {
    if (
      isPdfVisible &&
      (screenshot?.dataUrl || data.geometry === null) &&
      cutAndFill &&
      cutAndFillImage
    ) {
      if (pdfContainerRef.current) {
        generatePdf(pdfContainerRef.current);
        setIsPdfVisible(false);
      }
    }
  }, [
    isPdfVisible,
    screenshot?.dataUrl,
    generatePdf,
    cutAndFill,
    cutAndFillImage,
  ]);

  const handleFocusOnObject = () => {
    if (!generalGeometry) return;

    dispatch(
      focusOnFeature({
        ...data,
        geometry: generalGeometry,
        label: "",
        addMarker: false,
        changeStyle: false,
        zoomAdjustment: -1,
      }),
    );
  };

  return (
    <>
      <div className={styles.card}>
        <button className={styles.positionIcon} onClick={handleFocusOnObject}>
          <img src={positionIcon} alt="position" />
        </button>
        <span className={styles.header}>
          {label}: {data.number}
        </span>
        <ul className={styles.description}>
          <li>
            {t("cardMultiplePlotList.city")}: {data.city}
          </li>
          <li>
            {t("cardMultiplePlotList.landUse")}: {data.typeOfUse}
          </li>
          <li>
            {t("cardMultiplePlotList.totalArea")}: {data.area} m²
          </li>
          <li>{t("cardMultiplePlotList.totalEstimatedRent")}: 0.000 OMR</li>
        </ul>
        <div className={styles.buttons}>
          <Button onClick={handleClickGetSummary} type="outline">
            {t("cardMultiplePlotList.getSummary")}
          </Button>
          <Button type="withIcon">
            {t("cardMultiplePlotList.investHere")}
          </Button>
        </div>
      </div>
      {isPdfVisible &&
        pdfPortal &&
        createPortal(
          <div ref={pdfContainerRef}>
            <CardSummary
              infrastructure={infrastructureData}
              cutAndFill={cutAndFill}
              cutAndFillImage={cutAndFillImage}
              screenshot={screenshot?.dataUrl ?? null}
              item={data}
              type="MultiplePlotsList"
            />
          </div>,
          pdfPortal,
        )}
    </>
  );
};

export default CardMultiplePlotList;
